import {
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useCAWContext } from '../CAW';
import classnames from 'classnames';
import RoomOptions from './RoomOptions';
import { getRTL, getUniqueId, Room, translate } from '@micro-apps/env';
import { useBlurDetector, useWindowSize } from '@fshr/hooks';
import Dialog from '../../dialog';
import { createPortal } from 'react-dom';

export const buildSelectedOptionsLabel = (
	roomArray: Room[],
	roomSeparator,
	alwayIncludeChildren
) => {
	const roomsValue = roomArray.reduce(
		(acc, a) => {
			const adults = acc.numberOfAdults + (Number(a.adults) || 0);
			const children = acc.numberOfChildren + (Number(a.children) || 0);
			return {
				numberOfRooms: acc.numberOfRooms + 1,
				numberOfAdults: adults,
				numberOfChildren: children,
			};
		},
		{
			numberOfRooms: 0,
			numberOfAdults: 0,
			numberOfChildren: 0,
		}
	);
	let occStr =
		roomsValue.numberOfRooms.toString() +
		' ' +
		(roomsValue.numberOfRooms > 1 ? translate('Rooms') : translate('Room')) +
		` ${roomSeparator} `;
	occStr =
		occStr +
		roomsValue.numberOfAdults.toString() +
		' ' +
		(roomsValue.numberOfAdults > 1 ? translate('Adults') : translate('Adult'));
	if (alwayIncludeChildren || roomsValue.numberOfChildren > 0) {
		occStr =
			occStr +
			', ' +
			roomsValue.numberOfChildren.toString() +
			' ' +
			(!roomsValue.numberOfChildren || roomsValue.numberOfChildren > 1
				? translate('Children')
				: translate('Child'));
	}

	return occStr;
};

export default function GuestDropdown(): ReactElement<any> {
	const { planStore, isPPC, isDesktop, isHeroVariant } = useCAWContext();
	const [width, height] = useWindowSize();
	const isTallViewport = isHeroVariant && (height || 0) >= 1024;
	const portalRef = useRef<HTMLDivElement>(null);
	const anchorRef = useRef<HTMLDivElement>(null);
	const field = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const usesPopup = isDesktop;

	const closeDialog = useCallback((options?: { restoreFocus?: boolean }) => {
		setIsOpen(false);
		if (options?.restoreFocus) {
			requestAnimationFrame(() => {
				triggerRef.current?.focus({ preventScroll: true });
			});
		}
	}, []);
	const closeDialogFromEvent = useCallback(() => {
		closeDialog();
	}, [closeDialog]);
	const closeAndRestoreFocus = useCallback(() => {
		closeDialog({ restoreFocus: true });
	}, [closeDialog]);

	const openDialog = useCallback(() => {
		if (isOpen) return;
		window.dispatchEvent(new Event('closeCAWDialogs'));
		setIsOpen(true);
	}, [isOpen]);
	const closeDialogOnBlur = useCallback(() => {
		if (usesPopup) {
			return;
		}
		closeDialog();
	}, [closeDialog, usesPopup]);
	const { pause, resume } = useBlurDetector(field, isOpen, closeDialogOnBlur);

	useLayoutEffect(() => {
		if (!isOpen || !usesPopup) return;
		const el = anchorRef.current;
		if (!el || !portalRef.current) return;
		if (!isHeroVariant) {
			portalRef.current.style.top = '100%';
			portalRef.current.style.left = getRTL() ? 'auto' : '0';
			portalRef.current.style.right = getRTL() ? '0' : 'auto';
			return;
		}
		const anchorPos = el.getBoundingClientRect();
		const offsetFromTop = anchorPos.top - (document.body?.getBoundingClientRect()?.top || 0);
		portalRef.current.style.top = `${offsetFromTop}px`;
		portalRef.current.style.left = `${anchorPos.left}px`;
		if (isHeroVariant && containerRef.current) {
			containerRef.current.style.maxHeight = `${offsetFromTop + 40}px`;
		}
	}, [isOpen, usesPopup, isHeroVariant]);

	useEffect(() => {
		window.addEventListener('closeCAWDialogs', closeDialogFromEvent);
		if (isHeroVariant) {
			window.addEventListener('scroll', closeDialogFromEvent);
		}
		return () => {
			window.removeEventListener('closeCAWDialogs', closeDialogFromEvent);
		};
	}, [closeDialogFromEvent]);

	useEffect(() => {
		if (!usesPopup) {
			return;
		}
		const onPointerDownOutside = (event: MouseEvent | TouchEvent) => {
			if (!isOpen) {
				return;
			}
			const target = event.target as Node | null;
			const dialogEl = containerRef.current;
			if (!target || !field.current || !dialogEl) {
				return;
			}
			const dialogWrapper = dialogEl.querySelector('.Occupancy-dialog');
			const triggerEl = triggerRef.current;
			const clickedInsideDialog = !!dialogWrapper && dialogWrapper.contains(target);
			const clickedOnTrigger = !!triggerEl && triggerEl.contains(target);

			// Close whenever the click is outside the visible popup panel and outside the trigger.
			if (!clickedInsideDialog && !clickedOnTrigger) {
				closeDialog();
			}
		};
		document.addEventListener('mousedown', onPointerDownOutside);
		document.addEventListener('touchstart', onPointerDownOutside);
		return () => {
			document.removeEventListener('mousedown', onPointerDownOutside);
			document.removeEventListener('touchstart', onPointerDownOutside);
		};
	}, [closeDialog, isOpen, usesPopup]);

	useEffect(() => {
		if (!usesPopup || !isOpen) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			closeDialog({ restoreFocus: true });
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [closeDialog, isOpen, usesPopup]);

	const containerID = useMemo(
		() => `Guest-container-${getUniqueId()}${isPPC ? '-ppc' : ''}`,
		[isPPC]
	);
	const dialogId = useMemo(() => `Occupancy-${getUniqueId()}`, []);
	const occupancyAnchorId = `${containerID}-occupancy-anchor`;
	const portalRootId = `${containerID}-portal`;
	const screenReaderSummaryText = buildSelectedOptionsLabel(planStore!.search!.rooms!, '-', false);
	const labelClasses = classnames(
		'OccupancyField_label OccupancyField_label--active m-0 opacity-100 h-auto w-auto transition-none',
		'static text-label'
	);

	return (
		<div
			tabIndex={-1}
			className={classnames(
				'OccupancyField_row OccupancyField relative flex flex-col spacing-stack-2xs',
				{
					active: isOpen,
					'block w-full': isPPC,
					'w-[270px]': !isHeroVariant && !isPPC && !isTallViewport,
					'w-full': isTallViewport,
					'max-w-[270px] xl:max-w-full xl:min-w-[270px]': isHeroVariant && !isTallViewport,
				}
			)}
			ref={field}
			id={containerID}
		>
			<label
				htmlFor="occupancyId"
				id="occupancy-label-id"
				className={labelClasses}
				aria-label={screenReaderSummaryText}
				aria-live="polite"
			>
				<span className={isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'}>
					{translate('Guests')}
				</span>
			</label>
			<div
				ref={anchorRef}
				id={occupancyAnchorId}
				className={classnames('relative w-full', {
					'w-full': !isPPC,
					'max-w-[270px] xl:max-w-full xl:min-w-[270px]': isHeroVariant && !isTallViewport,
				})}
			>
				<div
					className={classnames('OccupancyField_row text-body3', {
						'block w-full': isPPC,
						'w-full': !isPPC && !isTallViewport,
						'max-w-[270px] xl:max-w-full xl:min-w-[270px]': isHeroVariant && !isTallViewport,
					})}
				>
					<div
						id="occupancyId"
						ref={triggerRef}
						className={classnames(
							'OccupancyField_container relative flex h-10 w-full items-center pr-8 pl-[10px] leading-none whitespace-nowrap overflow-hidden text-ellipsis text-[16px]',
							{
								'pl-8 pr-[10px]': getRTL(),
								'shows-options': isOpen,
								'bg-white w-full': isPPC && !isHeroVariant,
								'bg-surface-form': !isPPC && !isHeroVariant,
								'text-text-secondary border-border-subtle': !isHeroVariant,
								'text-text-inverse-heading border-border-inverse bg-img-overlay-40 backdrop-blur-[15px]':
									isHeroVariant,
							},
							'border-[1px] rounded-[4px]'
						)}
						tabIndex={0}
						onClick={openDialog}
						onFocus={usesPopup ? undefined : openDialog}
						onKeyDown={e => {
							if (usesPopup && (e.key === 'Enter' || e.key === ' ')) {
								e.preventDefault();
								openDialog();
							}
						}}
						role="button"
					>
						{screenReaderSummaryText}
						<span
							aria-hidden="true"
							className={classnames(
								'pointer-events-none absolute right-3 top-1/2 h-[8px] w-[8px] -translate-y-1/2 rotate-45 border-r border-b',
								{
									'right-[unset] left-3': getRTL(),
									'border-text-primary': !isHeroVariant,
									'border-text-inverse-heading': isHeroVariant,
								}
							)}
						/>
					</div>
				</div>

				{createPortal(
					<div
						id={portalRootId}
						className="tailwind-root CAW"
						ref={portalRef}
						style={{ position: 'absolute' }}
					>
						<Dialog
							isOpen={isOpen}
							onClose={closeAndRestoreFocus}
							title=""
							id={dialogId}
							disablePortal
							containerRef={containerRef}
							classNames={{
								container: classnames(
									'Occupancy-container',
									'absolute left-0 z-[1001] overflow-visible',
									{
										'left-[unset] right-0': getRTL(),
										'top-[100%]': !isHeroVariant,
										'-bottom-[40px] bg-surface-raised': isHeroVariant,
									}
								),
								overlay: 'hidden',
								dialog: 'Occupancy-dialog',
							}}
							contentClassName={classnames(
								'Occupancy-content Occupancy-wrapper relative w-[350px] max-w-[350px] max-h-[400px] overflow-y-auto bg-surface-raised border border-border-subtle',
								{
									'shadow-raised': !usesPopup,
									'shadow-[0_5px_15px_0_rgba(3,3,3,0.12)]': usesPopup,
								}
							)}
						>
							<RoomOptions pause={pause} resume={resume} onClose={closeAndRestoreFocus} />
						</Dialog>
					</div>,
					anchorRef.current && !isHeroVariant ? anchorRef.current : document.body
				)}
			</div>
		</div>
	);
}
